import { api, authHeaders, describeError } from "./api";
import type { DocumentLayout, LayoutBlock, LayoutField } from "./api";

/** แม่แบบเอกสารที่ AI สร้างจากไฟล์ตัวอย่าง (ADR-0028) — `/v1/admin/document-types/{id}/layout*` */

/** null = ชนิดนี้ยังไม่เคยสร้างแม่แบบ */
export async function fetchLayout(
  documentTypeId: string,
): Promise<DocumentLayout | null> {
  const { data, error, response } = await api.GET(
    "/v1/admin/document-types/{id}/layout",
    {
      params: { path: { id: documentTypeId } },
      headers: authHeaders(),
    },
  );
  if (response.status === 404) return null;
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

/** ไฟล์ที่ AI อ่านได้ — ชนิดเดียวกับที่ server รับ */
export const SAMPLE_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
];

/** ส่งไฟล์ตัวอย่างให้ AI สร้างร่าง — ใช้เวลาหลายสิบวินาที */
export async function generateLayout(
  documentTypeId: string,
  file: File,
): Promise<DocumentLayout> {
  const { data, error, response } = await api.POST(
    "/v1/admin/document-types/{id}/layout/generate",
    {
      params: { path: { id: documentTypeId }, query: { file_name: file.name } },
      body: file as unknown as string,
      bodySerializer: (b: unknown) => b as BodyInit,
      headers: {
        ...authHeaders(),
        "Content-Type": file.type || "application/pdf",
      },
    },
  );
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

/** ใช้แบบฟอร์ม PDF ที่อัปโหลดไว้แล้วเป็นตัวอย่าง */
export async function generateLayoutFromTemplate(
  documentTypeId: string,
): Promise<DocumentLayout> {
  const { data, error, response } = await api.POST(
    "/v1/admin/document-types/{id}/layout/generate",
    {
      params: { path: { id: documentTypeId }, query: { from_template: true } },
      headers: authHeaders(),
    },
  );
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

export async function saveLayoutDraft(
  documentTypeId: string,
  blocks: LayoutBlock[],
  fields: LayoutField[],
  onePage: boolean,
  version: number,
): Promise<DocumentLayout> {
  const { data, error, response } = await api.PUT(
    "/v1/admin/document-types/{id}/layout",
    {
      params: { path: { id: documentTypeId } },
      body: { blocks, fields, one_page: onePage, version },
      headers: authHeaders(),
    },
  );
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

export async function layoutPreviewURL(
  documentTypeId: string,
  which: "draft" | "active",
): Promise<string> {
  const { data, error, response } = await api.GET(
    "/v1/admin/document-types/{id}/layout/preview/url",
    {
      params: { path: { id: documentTypeId }, query: { which } },
      headers: authHeaders(),
    },
  );
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.url;
}

export async function activateLayout(
  documentTypeId: string,
  version: number,
): Promise<DocumentLayout> {
  const { data, error, response } = await api.POST(
    "/v1/admin/document-types/{id}/layout/activate",
    {
      params: { path: { id: documentTypeId } },
      body: { version },
      headers: authHeaders(),
    },
  );
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

export async function disableLayout(
  documentTypeId: string,
  version: number,
): Promise<DocumentLayout> {
  const { data, error, response } = await api.POST(
    "/v1/admin/document-types/{id}/layout/disable",
    {
      params: { path: { id: documentTypeId } },
      body: { version },
      headers: authHeaders(),
    },
  );
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}
